<script setup lang="ts">
import { onMounted } from 'vue';
import Page from '@/shared/components/layouts/Page.vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Dropdown from 'primevue/dropdown';
import { useToast } from 'primevue/usetoast';
import Paginator from '@/shared/components/ui/Paginator.vue';
import { addToast } from '@/shared/utils/toastMessages';
import { downloadCsv, generateTimestampedFileName } from '@/shared/utils/fileDownload';
import { useMaintenanceCompletenessReport } from '../composables/useMaintenanceCompletenessReport';
import { exportMaintenanceCompletenessPdf } from '../services/maintenanceCompletenessReportPdf';
import {
  buildCompletenessCsv,
  COMPLETENESS_METHOD_OPTIONS,
  COMPLETENESS_STATUS_OPTIONS,
} from '../utils/maintenanceCompletenessFormatting';
import CompletenessSummaryTiles from '../components/CompletenessSummaryTiles.vue';
import CompletenessGroupCard from '../components/CompletenessGroupCard.vue';
import AssetPerformanceStandardOptionSelector from '@/features/asset-hierarchy/components/Selectors/AssetPerformanceStandardOptionSelector.vue';
import MaintenanceAssetTypeSelector from '@/shared/components/selectors/MaintenanceAssetTypeSelector.vue';

definePage({ meta: { title: 'Maintenance Completeness Report' } });

const toast = useToast();
const {
  filters, searchInput, page, pageSize, isLoading, isExporting, error,
  groups, totals, methodSummaries, totalPages, totalGroups, hasActiveFilters, report,
  load, resetFilters, exportAll,
} = useMaintenanceCompletenessReport();

const pageSizeOptions = [25, 50, 100, 200].map((value) => ({ label: `${value} per page`, value }));

onMounted(() => void load());

async function runExport(kind: 'csv' | 'pdf'): Promise<void> {
  const failure = await exportAll(async (allGroups, snapshot) => {
    if (kind === 'csv') {
      downloadCsv(buildCompletenessCsv(allGroups), generateTimestampedFileName('maintenance-completeness', 'csv'));
    } else {
      await exportMaintenanceCompletenessPdf({
        totals: snapshot.totals,
        methodSummaries: snapshot.methodSummaries,
        groups: allGroups,
      });
    }
  });
  if (failure) {
    addToast(toast, { severity: 'error', summary: 'Export Failed', detail: failure });
  }
}
</script>

<template>
  <Page :fill="false">
    <div class="space-y-3">
      <div class="bg-background-card rounded-lg shadow-card overflow-hidden p-6">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex items-center">
            <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mr-4">
              <i class="fa-solid fa-wrench text-xl"></i>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-primary">Maintenance Completeness Report</h1>
              <p class="text-secondary">Requirements from every active source, grouped per asset, with coverage status</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <Button label="Refresh" icon="fa-solid fa-rotate" severity="secondary" outlined :disabled="isLoading" data-testid="refresh" @click="load" />
            <Button label="CSV" icon="fa-solid fa-file-csv" severity="secondary" outlined :disabled="isExporting || !report" data-testid="export-csv" @click="runExport('csv')" />
            <Button label="PDF" icon="fa-solid fa-file-pdf" severity="secondary" outlined :disabled="isExporting || !report" data-testid="export-pdf" @click="runExport('pdf')" />
          </div>
        </div>
      </div>

      <div v-if="error" class="bg-background-card rounded-lg shadow-card border border-error/40 p-4 flex items-center justify-between gap-3" role="alert">
        <span class="text-sm text-error">{{ error }}</span>
        <Button label="Retry" size="small" data-testid="retry" @click="load" />
      </div>

      <CompletenessSummaryTiles :totals="totals" :methods="methodSummaries" />

      <div class="bg-background-card rounded-lg shadow-card border border-border p-4 flex flex-wrap items-end gap-3">
        <label class="flex flex-col gap-1 text-xs text-text-light">
          Methods
          <MultiSelect v-model="filters.methods" :options="COMPLETENESS_METHOD_OPTIONS" option-label="label" option-value="value" placeholder="All methods" display="chip" class="w-72" />
        </label>
        <label class="flex flex-col gap-1 text-xs text-text-light">
          Coverage status
          <MultiSelect v-model="filters.coverageStatuses" :options="COMPLETENESS_STATUS_OPTIONS" option-label="label" option-value="value" placeholder="All statuses" display="chip" class="w-64" />
        </label>
        <div class="flex flex-col gap-1 text-xs text-text-light w-72">
          <label id="completeness-ops-label" for="completeness-ops-filter">Performance standard</label>
          <AssetPerformanceStandardOptionSelector
            v-model="filters.performanceStandardOption"
            input-id="completeness-ops-filter"
            aria-labelledby="completeness-ops-label completeness-ops-filter"
            placeholder="All performance standards"
            :allow-create="false"
            :confirm-inactive="false"
            clearable
          />
        </div>
        <div class="flex flex-col gap-1 text-xs text-text-light w-72">
          <label id="completeness-asset-type-label" for="completeness-asset-type-filter">Maintenance asset type</label>
          <MaintenanceAssetTypeSelector
            v-model="filters.maintenanceAssetType"
            input-id="completeness-asset-type-filter"
            aria-labelledby="completeness-asset-type-label completeness-asset-type-filter"
            placeholder="All maintenance asset types"
            :confirm-inactive="false"
            clearable
          />
        </div>
        <label class="flex flex-col gap-1 text-xs text-text-light">
          Search
          <InputText v-model="searchInput" maxlength="200" placeholder="Asset, analysis or recommendation" class="w-72" />
        </label>
        <label class="flex flex-col gap-1 text-xs text-text-light">
          Groups per page
          <Dropdown v-model="pageSize" :options="pageSizeOptions" option-label="label" option-value="value" class="w-40" />
        </label>
        <Button v-if="hasActiveFilters" label="Clear filters" severity="secondary" text @click="resetFilters" />
      </div>

      <div v-if="isLoading && groups.length === 0" class="text-sm text-text-light p-6">Loading report…</div>
      <div v-else-if="!error && groups.length === 0" class="bg-background-card rounded-lg shadow-card border border-border p-6 text-sm text-text-light">
        No requirements match the current filters.
      </div>
      <div v-else class="space-y-3" :aria-busy="isLoading">
        <CompletenessGroupCard v-for="group in groups" :key="`${group.scopeKind}-${group.scopeId}`" :group="group" />
      </div>

      <Paginator
        v-if="totalGroups > 0"
        v-model:current-page="page"
        :page-count="totalPages"
        :page-size="pageSize"
        :total-items="totalGroups"
        item-label="groups"
        @first="page = 1"
        @prev="page = Math.max(1, page - 1)"
        @next="page = Math.min(totalPages, page + 1)"
        @last="page = totalPages"
      />
    </div>
  </Page>
</template>
